import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { CalendarCheck, CalendarDays, ChevronLeft, Clock, Loader2, MapPin, Phone, UserRound, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pub = axios.create();  // no auth header – public page
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const pretty = (t) => { const [h, m] = t.split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
const longDate = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }); };
const jsDow = (d) => (d.getDay() + 6) % 7; // JS Sunday=0 → backend Monday=0

const Step = ({ n, label, active, done }) => (
  <div className={`flex items-center gap-2 text-xs sm:text-sm ${active ? "text-blue-900 font-semibold" : done ? "text-emerald-700" : "text-slate-400"}`}>
    <span className={`w-6 h-6 rounded-full grid place-items-center text-[11px] border ${active ? "bg-blue-900 text-white border-blue-900" : done ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-300"}`}>{done ? "✓" : n}</span>{label}
  </div>
);

export default function BookAppointment() {
  const [params] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState("");
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState(null); // { open, slots, reason }
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: params.get("name") || "", company: params.get("company") || "", email: params.get("email") || "", phone: params.get("phone") || "", address: "", notes: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    pub.get(`${API}/public/booking/config`).then((r) => {
      setCfg(r.data);
      const pre = r.data.types.find((t) => t.key === (params.get("type") || "").toLowerCase());
      if (pre) { setType(pre); setStep(2); }
    }).catch((e) => setErr(e.response?.status === 404 ? "Online booking is currently unavailable. Please call us to schedule." : "Could not load the booking page."));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!date || !type) return;
    setSlots(null); setTime("");
    pub.get(`${API}/public/booking/slots`, { params: { date: iso(date), type: type.key } }).then((r) => setSlots(r.data)).catch(() => setSlots({ open: false, slots: [], reason: "Could not load times" }));
  }, [date, type]);

  const { minDay, maxDay } = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const max = new Date(today); max.setDate(max.getDate() + (cfg?.horizon_days || 30));
    return { minDay: today, maxDay: max };
  }, [cfg]);
  const closed = (d) => d < minDay || d > maxDay || (type ? !type.days.includes(jsDow(d)) : false);
  const firstOpen = useMemo(() => { const d = new Date(minDay); for (let i = 0; i <= (cfg?.horizon_days || 30); i++) { if (!closed(d)) return new Date(d); d.setDate(d.getDate() + 1); } return minDay; }, [minDay, maxDay, type]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await pub.post(`${API}/public/booking`, { type: type.key, date: iso(date), time, ...form });
      setDone(data);
    } catch (ex) {
      const d = ex.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Something went wrong – please try again.");
      if (ex.response?.status === 409) { setStep(2); setDate(new Date(date)); }
    } finally { setBusy(false); }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-slate-100 py-6 px-3 sm:px-6" data-testid="book-page">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          <div className="bg-blue-900 text-white px-5 py-4 flex items-center gap-3">
            {cfg?.logo_b64 ? <img src={`data:image/jpeg;base64,${cfg.logo_b64}`} alt={cfg.company_name} className="h-10 w-auto rounded bg-white p-0.5" data-testid="book-logo" /> : <CalendarCheck className="w-8 h-8" />}
            <div><p className="font-semibold leading-tight" data-testid="book-company">{cfg?.company_name || "Schedule an appointment"}</p><p className="text-xs text-blue-100">Online scheduling</p></div>
          </div>
          <div className="p-5 sm:p-7">{children}</div>
        </div>
        {cfg?.company_phone && <p className="text-center text-xs text-slate-500 mt-4">Prefer to talk? Call {cfg.company_phone}</p>}
      </div>
    </div>
  );

  if (err && !cfg) return shell(<p className="text-slate-700 text-center py-8" data-testid="book-unavailable">{err}</p>);
  if (!cfg) return shell(<div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-700" /></div>);

  if (done) return shell(
    <div className="text-center py-4" data-testid="book-confirmation">
      <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-3" />
      <h1 className="text-2xl font-bold text-slate-900">Request received</h1>
      <p className="text-slate-600 mt-2">Thanks, {form.name.split(" ")[0]}! We've received your request for a <strong>{done.appointment.type}</strong> on</p>
      <p className="text-lg font-semibold text-blue-900 mt-1" data-testid="book-confirmation-when">{longDate(done.appointment.date)} at {pretty(done.appointment.time)}</p>
      <p className="text-sm text-slate-500 mt-3">A confirmation was sent to {done.email}. Our office will assign a technician and confirm the visit shortly.</p>
      <Button variant="outline" className="mt-6" onClick={() => window.location.reload()} data-testid="book-another">Book another appointment</Button>
    </div>
  );

  return shell(
    <>
      <div className="flex flex-wrap gap-3 sm:gap-6 mb-6">
        <Step n={1} label="Service" active={step === 1} done={step > 1} /><Step n={2} label="Date & time" active={step === 2} done={step > 2} /><Step n={3} label="Your details" active={step === 3} />
      </div>
      {cfg.intro && step === 1 && <p className="text-sm text-slate-600 mb-4" data-testid="book-intro">{cfg.intro}</p>}

      {step === 1 && (
        <div className="grid gap-3" data-testid="book-step-type">
          <h1 className="text-xl font-bold text-slate-900">What do you need?</h1>
          {cfg.types.map((t) => (
            <button key={t.key} type="button" onClick={() => { setType(t); setDate(null); setStep(2); }} className="text-left rounded-xl border border-slate-200 hover:border-blue-700 hover:bg-blue-50 p-4 transition-colors" data-testid={`book-type-${t.key}`}>
              <p className="font-semibold text-slate-900">{t.label}</p><p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Clock className="w-3 h-3" /> about {t.duration} minutes</p>
            </button>
          ))}
        </div>
      )}

      {step === 2 && type && (
        <div data-testid="book-step-date">
          <button type="button" onClick={() => setStep(1)} className="text-xs text-blue-800 flex items-center gap-1 mb-2" data-testid="book-back-to-type"><ChevronLeft className="w-3 h-3" /> Change service</button>
          <h1 className="text-xl font-bold text-slate-900">Pick a day for your {type.label.toLowerCase()}</h1>
          <div className="grid sm:grid-cols-[auto,1fr] gap-5 mt-4">
            <div className="rounded-xl border border-slate-200 p-1 bg-white justify-self-center" data-testid="book-calendar">
              <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} disabled={closed} fromDate={minDay} toDate={maxDay} defaultMonth={firstOpen} />
            </div>
            <div>
              {!date && <p className="text-sm text-slate-500 mt-2">Closed days are greyed out. Choose a day to see open times.</p>}
              {date && <p className="text-sm font-medium text-slate-800 flex items-center gap-1"><CalendarDays className="w-4 h-4 text-blue-800" /> {longDate(iso(date))}</p>}
              {date && !slots && <Loader2 className="w-5 h-5 animate-spin text-blue-700 mt-3" />}
              {date && slots && !slots.open && <p className="text-sm text-amber-700 mt-3" data-testid="book-no-slots">{slots.reason || "No openings that day."}</p>}
              {date && slots && slots.open && slots.slots.length === 0 && <p className="text-sm text-amber-700 mt-3" data-testid="book-no-slots">No openings left that day – please try another.</p>}
              {date && slots?.open && slots.slots.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-3" data-testid="book-slots">
                  {slots.slots.map((s) => <button key={s} type="button" onClick={() => setTime(s)} className={`rounded-lg border px-2 py-2 text-sm ${time === s ? "bg-blue-900 text-white border-blue-900" : "border-slate-200 hover:border-blue-700"}`} data-testid={`book-slot-${s}`}>{pretty(s)}</button>)}
                </div>
              )}
              <Button className="mt-5 w-full bg-blue-900 hover:bg-blue-800" disabled={!time} onClick={() => setStep(3)} data-testid="book-continue">Continue</Button>
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <form onSubmit={submit} className="space-y-4" data-testid="book-step-contact">
          <button type="button" onClick={() => setStep(2)} className="text-xs text-blue-800 flex items-center gap-1" data-testid="book-back-to-date"><ChevronLeft className="w-3 h-3" /> Change time</button>
          <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 text-sm text-blue-900" data-testid="book-summary"><strong>{type.label}</strong> · {longDate(iso(date))} at {pretty(time)}</div>
          <h1 className="text-xl font-bold text-slate-900">Your details</h1>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label htmlFor="bk-name">Your name *</Label><div className="relative"><UserRound className="w-4 h-4 absolute left-2.5 top-3 text-slate-400" /><Input id="bk-name" className="pl-8" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="book-name" /></div></div>
            <div><Label htmlFor="bk-company">Company</Label><Input id="bk-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="book-company-input" /></div>
            <div><Label htmlFor="bk-email">Email *</Label><Input id="bk-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="book-email" /></div>
            <div><Label htmlFor="bk-phone">Phone</Label><div className="relative"><Phone className="w-4 h-4 absolute left-2.5 top-3 text-slate-400" /><Input id="bk-phone" className="pl-8" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="book-phone" /></div></div>
          </div>
          <div><Label htmlFor="bk-address">Service address{type.key === "onsite" ? "" : " (optional)"}</Label><div className="relative"><MapPin className="w-4 h-4 absolute left-2.5 top-3 text-slate-400" /><Input id="bk-address" className="pl-8" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="book-address" /></div></div>
          <div><Label htmlFor="bk-notes">What can we help with?</Label><Textarea id="bk-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="book-notes" /></div>
          <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" data-testid="book-honeypot" />
          {err && <p className="text-sm text-red-600" data-testid="book-error">{err}</p>}
          <Button type="submit" className="w-full bg-blue-900 hover:bg-blue-800" disabled={busy} data-testid="book-submit">{busy ? "Sending…" : "Request appointment"}</Button>
          <p className="text-[11px] text-slate-500 text-center">We'll email you a confirmation. A technician is assigned by our office before the visit.</p>
        </form>
      )}
    </>
  );
}
